// WHICH BADGE KEYS DIE WITH AN ARCHIVED SESSION — the door for the other half of
// the unread-badge invariant (#3148).
//
// `getDmUnreadCounts` only counts a DM whose sender still has a surface:
// `live_senders` (a workspace member, or an `agent_sessions` row with
// `archived_at IS NULL`, by agent id or cybo id) OR a row in `cybos` for the
// canonical peer. Archiving the last live session therefore takes the peer out of
// `live_senders` — and if nothing in `cybos` names it, the server can never
// return that key again. The Direct Messages list goes the same way (it walks the
// cybo roster plus the viewer's own sessions), so the red number is left with
// nothing to reconcile it and nothing to click: a badge with no door.
//
// The rule below is the CLIENT SIDE OF THAT SAME PREDICATE, deliberately written
// in the server's two terms so the two cannot drift:
//
//   • BOTH keys the conversation can be badged under die — the raw session id
//     (badged by a `dm_broadcast` that still carries a per-summon id) and the
//     canonical peer id — because `live_senders` held each of them only while
//     `archived_at IS NULL`;
//   • EXCEPT an id that is a cybo of this workspace. A cybo keeps BOTH its server
//     arm (`cy.id IS NOT NULL`, which ignores archiving entirely) and its DM row
//     (built from the cybo roster, session or not), so retiring it would throw
//     away a badge that is still reconcilable and still clickable — and on a
//     transport whose `fetch_unread` omits `dmCounts` that would be a permanently
//     LOST unread, the exact mirror of the bug this fixes.
//
// Pure + dependency-light so it is unit-testable under plain-node vitest — the
// same split cybo-dm-peer-id.ts and agent-dm-surface.ts were extracted for.

/**
 * The notification keys to retire when `agentId`'s session is archived/cleared.
 * `canonicalPeerId` is that agent's canonical DM peer id (its cybo id when
 * cybo-bound, else the agent id itself — see cybo-dm-peer-id.ts), and `cyboIds`
 * is the workspace's cybo roster.
 *
 * Returns at most two ids and never duplicates. An empty `agentId` retires
 * nothing rather than guessing, and a cybo-bound conversation retires nothing at
 * all — its door is still open.
 */
export function retiredBadgeKeysForArchivedAgent(
  agentId: string,
  canonicalPeerId: string,
  cyboIds: readonly string[],
): string[] {
  if (!agentId) return [];
  const keys: string[] = [];
  for (const id of [agentId, canonicalPeerId]) {
    if (!id || keys.includes(id) || cyboIds.includes(id)) continue;
    keys.push(id);
  }
  return keys;
}
